import { FC, useCallback, useState } from 'react';
import { CardContent } from '@mui/material';
import { UseMutationResult } from '@tanstack/react-query';
import { Hooks } from '@wellstone-solutions/common';
import { TextField, Typography } from '@wellstone-solutions/web';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { CardHeader } from '@components/ui/Card';
import { PasswordField } from '@components/ui/Form';
import { Link } from '@components/ui/Link';
import {
  PRIVACY_POLICY_URL,
  TERMS_OF_USE_URL,
} from '@constants/external-links';
import { Form } from '@modules/auth/components/Form';
import { SIGN_UP_FORM_ID } from '@modules/auth/constants';
import { useCreateRegistration } from '@modules/auth/queries';
import { SignUpSchema } from '@modules/auth/schemas/sign-up-schema';
import { useAuthStore } from '@modules/auth/store/auth-store';

import type { SignUpSchemaType } from '@modules/auth/schemas/sign-up-schema';
import type { Login, Register } from '@wellstone-solutions/api/v2';
import type { AxiosError, AxiosResponse } from 'axios';

type SignUpFormType = ReturnType<typeof Hooks.useForm<SignUpSchemaType>>;

const createHandleSignUpSuccess = (
  setSuccessMessage: (msg: string) => void,
  t: (key: string) => string,
  navigate: (path: string) => void,
  setRegistrationSuccess: () => void,
  setUserInfo: (info: { email?: string }) => void
) => {
  return (response: AxiosResponse<Register | Login>) => {
    setSuccessMessage(t('auth.signup.success_message'));
    // Store email in Zustand
    setUserInfo({ email: response.data.email });
    // Update registration state
    setRegistrationSuccess();
    // Navigate without email parameter
    navigate('/auth/verify-email');
  };
};

const SignUp: FC = () => {
  const [successMessage, setSuccessMessage] = useState<string | undefined>(
    undefined
  );
  const { t } = useTranslation();
  const mutation = useCreateRegistration() as UseMutationResult<
    AxiosResponse<Login | Register>,
    AxiosError<{ [key: string]: string[] }>,
    Login | Register
  >;
  const navigate = useNavigate();

  // Get Zustand actions
  const { startRegistration, setRegistrationSuccess, setUserInfo } =
    useAuthStore();

  const handleSuccess = useCallback(
    // eslint-disable-next-line react-compiler/react-compiler
    createHandleSignUpSuccess(
      setSuccessMessage,
      t,
      navigate,
      setRegistrationSuccess,
      setUserInfo
    ),
    [setSuccessMessage, t, navigate, setRegistrationSuccess, setUserInfo]
  );

  return (
    <>
      <CardHeader
        title={t('auth.signup.title')}
        subheaders={[
          <Trans key="auth.signup.subheader" i18nKey="auth.signup.subheader">
            Welcome! We're glad to have you here.
          </Trans>,
          <Trans
            key="auth.signup.have_account"
            i18nKey="auth.signup.have_account">
            Already have an account?{' '}
            <Link to="/auth/login" key="login-link">
              Log In
            </Link>
          </Trans>,
        ]}
      />
      <CardContent>
        <Form
          formId={SIGN_UP_FORM_ID}
          schema={SignUpSchema}
          initialValues={{
            email: '',
            password: '',
            passwordConfirmation: '',
          }}
          message={successMessage}
          mutation={mutation}
          mutateOptions={{
            onMutate: () => {
              startRegistration();
            },
            onSuccess: handleSuccess,
          }}>
          {(form) => {
            const formik = form as SignUpFormType;

            const passwordConfirmed = () => {
              if (formik.errors.password || formik.errors.passwordConfirmation)
                return false;

              return (
                formik.values.password &&
                formik.values.passwordConfirmation &&
                formik.values.password === formik.values.passwordConfirmation
              );
            };

            const emailFieldHelperText = () => {
              if (formik.submitCount < 1) {
                return t('auth.signup.email_helper_text');
              }

              return formik.errors.email ?? t('auth.signup.email_helper_text');
            };

            const passwordFieldHelperText = () => {
              if (!formik.touched.password) {
                return t('auth.signup.password_helper_text');
              }

              if (formik.errors.password) return formik.errors.password;

              return t('auth.signup.password_success');
            };

            const passwordConfirmationFieldHelperText = () => {
              if (!formik.touched.passwordConfirmation) {
                return t('auth.signup.password_confirmation_helper_text');
              }

              if (formik.errors.passwordConfirmation)
                return formik.errors.passwordConfirmation;

              return t('auth.signup.password_confirmation_success');
            };

            return (
              <>
                <TextField
                  fullWidth
                  formField="email"
                  label={t('auth.email')}
                  type="email"
                  id="email"
                  inputProps={{ maxLength: 128 }}
                  onChange={formik.handleFieldChange('email')}
                  value={formik.values.email}
                  error={formik.submitCount > 0 && Boolean(formik.errors.email)}
                  helperText={emailFieldHelperText()}
                />
                <PasswordField
                  fullWidth
                  formField="password"
                  label={t('auth.password')}
                  id="password"
                  inputProps={{ maxLength: 128 }}
                  onChange={formik.handleFieldChange('password')}
                  value={formik.values.password}
                  error={
                    formik.touched.password && Boolean(formik.errors.password)
                  }
                  helperText={passwordFieldHelperText()}
                  sx={passwordConfirmed() ? styles.success : undefined}
                />
                <PasswordField
                  fullWidth
                  formField="passwordConfirmation"
                  label={t('auth.signup.password_confirmation')}
                  id="password-confirmation"
                  inputProps={{ maxLength: 128 }}
                  onChange={formik.handleFieldChange('passwordConfirmation')}
                  value={formik.values.passwordConfirmation}
                  error={
                    formik.touched.passwordConfirmation &&
                    Boolean(formik.errors.passwordConfirmation)
                  }
                  helperText={passwordConfirmationFieldHelperText()}
                  sx={passwordConfirmed() ? styles.success : undefined}
                />
              </>
            );
          }}
        </Form>
      </CardContent>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ textAlign: 'center' }}>
        <Trans
          i18nKey="auth.signup.legal"
          components={[
            <Link target="_blank" to={TERMS_OF_USE_URL} key="terms" />,
            <Link target="_blank" to={PRIVACY_POLICY_URL} key="privacy" />,
          ]}
        />
      </Typography>
    </>
  );
};

const styles = {
  success: {
    '& .MuiFormHelperText-root': {
      color: 'success.main',
    },
  },
};

export { SignUp };
